import { Component, computed, effect, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { WaitingListClientUpsertDialogStore } from './waiting-list-client-upsert-dialog.store';
import { BaseDialogComponent } from '../../../kit/base-dialog/base-dialog.component';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { AppFormGroup } from '../../../../utils/form.utils';
import { fb } from '../../../../modules/shared/shared.global-service';
import { WaitingListObject } from '@libs/types/src/services/object-type/waiting-list.object';
import {
    contractDetailFrag,
    createWaitingListItemFrag,
    updateWaitingListItemFrag,
    upsertContractOffersFrag,
} from './waiting-list-client-upsert-dialog.query';
import { BaseDialogService } from '../../../kit/base-dialog/base-dialog-service';
import { contentGlobalSuccess } from '../../../../modules/content/content.error';
import { isInvalid } from '../../../../modules/shared/helpers/validate';
import { WaitingListUpdateInput } from '@libs/types/src/services/input-type/waiting-list-update.input';
import { Validators } from '@angular/forms';
import { WaitingListCreateInput } from '@libs/types/src/services/input-type/waiting-list-create.input';
import {
    SocialServiceSelectOfferComponent,
} from '../../work/social-service-select-offer/social-service-select-offer.component';
import { SocialServiceOperationEnum } from '@libs/types/src/services/enum/social-service-operation.enum';
import { WorkOfferUpsertInput } from '@libs/types/src/services/input-type/work/work-offer.upsert-input';
import {
    SelectPersonComponent,
} from '../../../../modules/content/components/common/select-person/select-person.component';
import {
    WorkDetailSelectServiceComponent,
} from '../../../medical-service/work/work-detail/work-detail-select-service/work-detail-select-service.component';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { getFolderByPersonFrag } from '../../../medical-service/work/pages/work-visit-page/work-visit-page.query';
import { FilterInput } from '@it2go/data-grid';
import { TranslatePipe } from '@ngx-translate/core';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import {
    FolderIndividualPlanOfferObject,
} from '@libs/types/src/services/object-type/folder/folder-individual-plan-offer.object';
import { Chip } from '@openng/optimus-ui/chip';
import { ColorContrastPipe } from '../../../../modules/shared/pipes/color-contrast.pipe';
import {
    DEFAULT_TAG_COLOR,
} from '../../setting/service-settings-offers-page/service-settings-offer-detail-page/service-settings-offer-detail-page.component';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { NgStyle } from '@angular/common';
import { isDateBefore } from '@libs/types/src/common/utils/date.utils';
import { ButtonComponent } from '../../../kit/button/button.component';
import { WarnFloatMessageComponent } from '../../../kit/float-message/warn-float-message.component';
import { ErrorFloatMessageComponent } from '../../../kit/float-message/error-float-message.component';
import { ActionButtonsComponent } from '../../../kit/action-buttons/action-buttons.component';
import {
    FolderIndividualPlanOfferUpsertInput,
} from '@libs/types/src/services/input-type/folder/folder-individual-plan-offer.upsert.input';
import { WaitingListOfferUpsertInput } from '@libs/types/src/services/input-type/waiting-lists-offer.upsert-input';
import { FormatOfferDurationPipe } from '../../../../modules/shared/pipes/format-service-offer-duration.pipe';

@Component({
    selector: 'app-waiting-list-client-upsert-dialog',
    templateUrl: './waiting-list-client-upsert-dialog.component.html',
    styleUrls: ['./waiting-list-client-upsert-dialog.component.scss'],
    providers: [WaitingListClientUpsertDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        SocialServiceSelectOfferComponent,
        SelectPersonComponent,
        WorkDetailSelectServiceComponent,
        TranslatePipe,
        Chip,
        ColorContrastPipe,
        Tooltip,
        NgStyle,
        ButtonComponent,
        WarnFloatMessageComponent,
        ErrorFloatMessageComponent,
        ActionButtonsComponent,
        FormatOfferDurationPipe,
    ],
})
export class WaitingListClientUpsertDialogComponent extends BaseDialogComponent {

    public readonly store = inject(WaitingListClientUpsertDialogStore);
    public readonly dialogService = inject(BaseDialogService);

    protected readonly selectOfferComponent = viewChild(SocialServiceSelectOfferComponent);

    protected form = new AppFormGroup({
        serviceId: fb.control<number | null>(null, Validators.required),
        personId: fb.control<number | null>(null, Validators.required),
        dateFrom: fb.control<Date | null>(new Date(), Validators.required),
        dateTo: fb.control<Date | null>(null),
        offers: fb.control<WorkOfferUpsertInput[]>([]),
    });
    protected dateFromChanged = this.form.signalCodeChange<Date | null>('dateFrom');
    protected serviceIdChanged = this.form.signalCodeChange<number | null>('serviceId');
    protected personIdChanged = this.form.signalCodeChange<number | null>('personId');
    protected offersChanged = this.form.signalCodeChange<WorkOfferUpsertInput[]>('offers');

    protected prefix = 'waitingList.client.';

    protected item!: WaitingListObject;

    protected readonly personAdditionalFilter = computed<FilterInput[][] | null>(() => {
        const dateFrom = this.dateFromChanged();
        if (!dateFrom.value) return null;

        return [
            [{
                column: 'contractValidDate',
                operator: 'EQ',
                value: [dateFrom.value.toISOString()],
            }],
        ];
    });

    protected conflictedOffers = computed(() => {
        if (!this.store.folderId()) return { toClose: [], notToClose: [] };

        if (!this.store.contract()) return { toClose: [], notToClose: [] };

        const contractOffers: FolderIndividualPlanOfferObject[] = this.store.contract()?.offers || [];
        const selectedOffers = this.offersChanged().value?.map(x => x.offerId) || [];

        const conflicted = contractOffers.filter((it: FolderIndividualPlanOfferObject) => selectedOffers.includes(it.offerId));

        const dateFrom = this.dateFromChanged().value;

        return {
            contractId: this.store.contract()?.id || 0,
            toClose: conflicted.filter((it: FolderIndividualPlanOfferObject) => !it.dateTo),
            notToClose: conflicted.filter((it: FolderIndividualPlanOfferObject) => isDateBefore(dateFrom!, new Date(it.dateFrom)) || it.dateTo && isDateBefore(dateFrom!, new Date(it.dateTo))),
        };
    });

    constructor() {
        super();

        this.item = this.dialogConfig.data.item;

        if (this.item) {
            this.form.patchValue({
                serviceId: this.item.folder.serviceId,
                personId: this.item.folder.personId,
                dateFrom: this.item.dateFrom ? new Date(this.item.dateFrom) : null,
                dateTo: this.item.dateTo ? new Date(this.item.dateTo) : null,
            });
            if (this.item.offerId) {
                setTimeout(() => {
                    this.selectOfferComponent()?.preselectedOffers.set([{
                        offerId: this.item.offerId!,
                        applications: this.item.applications || 0,
                        operations: this.item.operations,
                        procedures: this.item.procedures,
                    } as any]);
                });
            }
        }

        this.store.onSuccess(() => this.fetchFolderContractOffers(),
            'upsertContractOffers',
        );

        effect(() => {
            this.fetchFolderByPerson();
        });

        effect(() => {
            this.fetchFolderContractOffers();
        });

        effect(() => {
            this.dateFromChanged();

            if (!this.item) {
                this.form.patchValue({ personId: null });
            }
        });
    }

    public override async save(_event: Event) {
        if (isInvalid(this.form) || this.conflictedOffers().toClose.length > 0 || this.conflictedOffers().notToClose.length > 0) return;

        const _data = {
            dateFrom: this.form.value.dateFrom?.toISOString() || null,
            dateTo: this.form.value.dateTo?.toISOString() || null,
        };

        if (this.item) {
            const offer = this.form.value.offers?.[0];
            const data = _data as WaitingListUpdateInput;
            data.id = this.item.id;
            data.offerId = offer?.offerId || null;
            data.applications = offer?.applications || 0;
            data.operations = offer?.operations || null;
            data.procedures = offer?.procedures || null;
            await this.store.api.updateWaitingListItem(data, updateWaitingListItemFrag);
            contentGlobalSuccess.next('common.updated');
        } else {
            const data = _data as WaitingListCreateInput;
            data.folderId = this.store.folderId() || 0;
            data.contractId = this.store.contract()?.id;
            data.entryDate = this.form.value.dateFrom?.toISOString() || (new Date()).toISOString();
            data.offers = this.form.value.offers?.map((it: WorkOfferUpsertInput) => {
                return <WaitingListOfferUpsertInput>{
                    offerId: it.offerId,
                    applications: it.applications,
                    operations: it.operations,
                    procedures: it.procedures,
                };
            }) || [];

            await this.store.api.createWaitingListItem(data, createWaitingListItemFrag);
            contentGlobalSuccess.next('common.created');
        }

        this.dialogRef.close();
    }

    protected async closeConflictedOffers() {
        await this.store.api.upsertContractOffers({
            contractId: this.conflictedOffers()?.contractId || 0,
            folderId: this.store.folderId() || 0,
            offers: this.conflictedOffers().toClose.map((it: FolderIndividualPlanOfferObject) => {
                return <FolderIndividualPlanOfferUpsertInput>{
                    id: it.id,
                    offerId: it.offerId,
                    frequency: it.frequency,
                    usualTime: it.usualTime,
                    dateFrom: it.dateFrom,
                    dateTo: this.form.value.dateFrom?.toISOString() || (new Date()).toISOString(),
                    operations: it.operations,
                    procedures: it.procedures,
                };
            }),
        }, upsertContractOffersFrag);
    }

    private async fetchFolderByPerson() {
        if (!this.serviceIdChanged().value) return;

        if (!this.personIdChanged().value) {
            this.store.folderId.set(null);
            return;
        }

        await this.store.api.getFolderByPerson({
            serviceId: this.serviceIdChanged().value || 0,
            personId: this.personIdChanged().value || 0,
        }, getFolderByPersonFrag);
    }

    private async fetchFolderContractOffers() {
        if (!this.store.folderId()) return;

        const filter = new FilterBuilder({
            folderId: this.store.folderId(),
        });
        filter.validAt(this.dateFromChanged().value, 'validFrom', 'computedValidTo');

        await this.store.api.getFolderContracts(
            filter.filter(),
            contractDetailFrag,
        );
    }

    protected readonly SocialServiceOperationEnum = SocialServiceOperationEnum;
    protected readonly ServiceTypeEnum = ServiceTypeEnum;
    protected readonly DEFAULT_TAG_COLOR = DEFAULT_TAG_COLOR;
    protected readonly isInvalid = isInvalid;
}
